import { message } from 'antd'
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Text } from '../../../constants/TextConstants'
import { epagAPI } from '../../../API/epagAPI'
import { useGetToken } from '../../../hooks/useGetToken'
import { exportHTMLtoPDF, exportHTMLtoWord } from './utils'
import { useSave } from '../../../Context/saveContext'
import { isAdminRole } from '../../../utils/utils'
import { PERMISSION_CODES } from '../../../constants/permissions'
import { BqButton, BqCheckbox, BqIcon, BqOption, BqRadio, BqRadioGroup, BqTooltip } from '@beeq/react'
import { Dialog, ExportSelect, H5Bold } from './styles'

export const ExportProfile = ({
  profileToExport,
  profilesToExport,
  setIsLoading,
  setLoadingText,
  disabled = false,
  counter,
  // use next two props to control from outside dialog's open state (ex: to use another opening dialog button)
  isOpenExternal,
  setIsOpenExternal
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [format, setFormat] = useState('pdf')
  const [template, setTemplate] = useState()
  const [isAnonymyzedTemplate, setIsAnonymyzedTemplate] = useState(false)
  const [exportScaleLevels, setExportScaleLevels] = useState(false)
  const { userProfile, userPermissions, permissionsQuantity } = useSave()
  const exportOwnProfile = userProfile?.email === (profileToExport?.email ?? null)
  const isAdmin = isAdminRole(userProfile?.userRole, userPermissions.length, permissionsQuantity)
  const canExport = exportOwnProfile || userPermissions.includes(PERMISSION_CODES.exportProfiles.code)
  const { token } = useGetToken()
  const [templateOptions, setTemplateOptions] = useState([])
  const isSingleUserExport = profilesToExport === undefined

  const isExternalControlledDialog = typeof setIsOpenExternal === 'function' && typeof isOpenExternal === 'boolean'

  useEffect(() => {
    let isMounted = true

    if (token !== null) {
      epagAPI
        .GetTemplateList(token)
        .then((answer) => {
          if (isMounted) {
            setTemplateOptions(answer)
            if (answer.length > 0) {
              setTemplate(answer[0].label)
            }
          }
        })
        .catch((error) => {
          console.error('error: ', error)
          setIsLoading(false)
        })
    }

    return () => {
      isMounted = false
    }
  }, [token])

  const onExport = () => {
    setIsOpen(false)
    if (isSingleUserExport) {
      downloadProfile(format)
    } else {
      downloadProfiles(format)
    }
  }

  const getTemplateId = (templateValue) => {
    return templateOptions.find((template) => template.label === templateValue)._id
  }

  const onIsAnonymyzedTemplateCheck = (e) => {
    setIsAnonymyzedTemplate(e.target.checked)
  }

  const onExportScaleLevelsCheck = (e) => {
    setExportScaleLevels(e.target.checked)
  }

  const getUppercaseInitials = (name, lastName) => {
    const fullName = `${lastName} ${name}`

    const initials = fullName
      .split(/[ -]/)
      .map((item) => item[0])
      .join('')

    return initials
  }

  const prepareFileForDownload = (profile, format, templateHTML, isAnonymyzedTemplate) => {
    const initials = getUppercaseInitials(profile.name, profile.lastName)
    const fullName = `${profile.name} ${profile.lastName}`
    let fileName = ''

    if (isAnonymyzedTemplate) {
      fileName = `Endava Profile - ${initials} - ${profile.role}`
    } else {
      fileName = `Endava Profile - ${profile.name} ${profile.lastName}`
    }

    switch (format) {
      case 'pdf':
        fileName += '.pdf'
        exportHTMLtoPDF(fileName, fullName, templateHTML.html, templateHTML)
        break
      case 'word':
        fileName += '.doc'
        exportHTMLtoWord(fileName, templateHTML.html, templateHTML)
        break
      default:
        throw new Error('Invalid export format')
    }
  }

  const downloadProfiles = (extension = 'pdf') => {
    setIsLoading(true)
    setLoadingText(Text.LoadingPDF)

    epagAPI
      .GetTemplates(
        token,
        getTemplateId(template),
        userProfile?.email,
        profilesToExport.selectedUsersToExport,
        profilesToExport.exportAllUsersExceptOf,
        profilesToExport.filters,
        isAdmin,
        isAnonymyzedTemplate,
        exportScaleLevels,
        extension
      )
      .then((answer) => {
        if (answer && answer.data.length > 0) {
          const contentType = answer.headers['content-type']
          const contentTypeIsTextPlain = /^text\/plain(; charset=utf-8)?$/.test(contentType)
          if (contentTypeIsTextPlain) {
            const templateHTML = answer.data[0]
            const profileToExport = {
              name: answer.data[0].userName,
              lastName: answer.data[0].userLastName,
              role: answer.data[0].userRole
            }
            prepareFileForDownload(profileToExport, extension, templateHTML, isAnonymyzedTemplate)
            setIsLoading(false)

            message.success(Text.AutoSuccessMessage, 7)
            setLoadingText(Text.GenericLoading)
          } else {
            const decodedBuffer = Buffer.from(answer.data, 'base64')
            const blob = new Blob([decodedBuffer], { type: 'application/zip' })
            const url = URL.createObjectURL(blob)
            const downloadLink = document.createElement('a')
            downloadLink.href = url
            downloadLink.download = 'Endava Profiles.zip'
            document.body.appendChild(downloadLink)
            downloadLink.click()
            document.body.removeChild(downloadLink)

            setIsLoading(false)
            message.success(Text.AutoSuccessMessage, 7)
            setLoadingText(Text.GenericLoading)
          }
        } else {
          message.error(Text.ErrorMessage, 5)
          setIsLoading(false)
        }
      })
      .catch((e) => {
        console.log(e)
        message.error(Text.ErrorMessage, 5)
        setIsLoading(false)
      })
  }

  function downloadProfile(extension = 'pdf') {
    setIsLoading(true)
    setLoadingText(Text.LoadingPDF)
    epagAPI
      .GetTemplate(
        token,
        getTemplateId(template),
        profileToExport.email,
        isAdmin,
        isAnonymyzedTemplate,
        exportScaleLevels
      )
      .then((answer) => {
        if (answer && answer.length > 0) {
          const templateHTML = answer[0]
          prepareFileForDownload(profileToExport, extension, templateHTML, isAnonymyzedTemplate)
          setIsLoading(false)
          message.success(Text.AutoSuccessMessage, 7)
          setLoadingText(Text.GenericLoading)
        } else {
          message.error(Text.ErrorMessage, 5)
          setIsLoading(false)
        }
      })
      .catch((e) => {
        console.log(e)
        message.error(Text.ErrorMessage, 5)
        setIsLoading(false)
      })
  }

  const handleDialogClose = () => {
    if (isExternalControlledDialog) {
      setIsOpenExternal(false)
    } else {
      setIsOpen(false)
    }
  }

  if (!canExport) return null
  return (
    <>
      {!isExternalControlledDialog && (
        <BqTooltip placement='top' displayOn='hover'>
          {Text.TheExportedProfileContentMayDiffer}
          <BqButton slot='trigger' data-testid='exportProfileBtn' disabled={disabled} onBqClick={() => setIsOpen(true)}>
            <BqIcon name='download-simple' slot='prefix' />
            {Text.ExportTo} {counter ? `(${counter})` : ''}
          </BqButton>
        </BqTooltip>
      )}

      <Dialog
        footer-apperance='highlight'
        open={isOpen || isOpenExternal}
        onBqClose={handleDialogClose}
        onBqCancel={handleDialogClose}>
        <H5Bold slot='title'>{Text.ExportModalTitle}</H5Bold>

        <div style={{ marginBottom: '16px' }}>
          <BqRadioGroup orientation='horizontal' value={format} onBqChange={(e) => setFormat(e.detail.value)}>
            <span slot='label'>{Text.ExportModalSelectFormat}</span>
            <BqRadio value='pdf'>{Text.ExportPDF}</BqRadio>
            <BqRadio value='word'>{Text.ExportMicrosoftWord}</BqRadio>
          </BqRadioGroup>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <ExportSelect
            disableClear={true}
            placeholder={Text.ExportModalSelectPlaceholder}
            data-testid='active-filter-select'
            value={template}
            onBqSelect={(e) => setTemplate(e.detail.value)}>
            <div slot='label' style={{ display: 'flex', flex: '1 1 0%' }}>
              <label style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                {Text.ExportModalSelectTemplate}
              </label>
            </div>
            {templateOptions.map((item, index) => {
              return (
                <BqOption key={index} value={item.label}>
                  {item.label}
                </BqOption>
              )
            })}
          </ExportSelect>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <BqCheckbox
            background-on-hover
            checked={isAnonymyzedTemplate}
            onBqChange={onIsAnonymyzedTemplateCheck}
            data-testid='anonymyzed-template'>
            {Text.ExportAnonymizedTemplate}
          </BqCheckbox>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <BqCheckbox
            background-on-hover
            checked={exportScaleLevels}
            onBqChange={onExportScaleLevelsCheck}
            data-testid='export-scale-levels'>
            {Text.ExportScaleLevels}
          </BqCheckbox>
        </div>

        <div slot='footer'>
          <BqButton
            style={{ paddingRight: '16px' }}
            variant='ghost'
            onBqClick={handleDialogClose}
            data-testid='export-cancel-btn'>
            {Text.ExportModalCancel}
          </BqButton>
          <BqButton variant='standard' slot='footer' onBqClick={onExport} data-testid='export-submit-btn'>
            {Text.ExportModalOk}
          </BqButton>
        </div>
      </Dialog>
    </>
  )
}

const validateProfilesToExport = (props, propName, componentName) => {
  const profileToExport = props.profileToExport
  const profilesToExport = props.profilesToExport

  if (profileToExport && profilesToExport) {
    return new Error(Text.ExportModalErrorProps)
  }

  return null
}

ExportProfile.propTypes = {
  setIsLoading: PropTypes.func.isRequired,
  setLoadingText: PropTypes.func.isRequired,
  profileToExport: validateProfilesToExport,
  profilesToExport: validateProfilesToExport,
  disabled: PropTypes.bool,
  counter: PropTypes.number,
  isOpenExternal: PropTypes.bool,
  setIsOpenExternal: PropTypes.func
}
